import { useEffect } from 'react'
import { Upload } from 'lucide-react'
import PageLayout from '../components/layout/PageLayout'
import ConnectorDataTable from '@/components/ConnectorDataTable'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { useAppStore } from '../store'
import ConnectorEditModal from '@/components/ConnectorEditModal'
import {
  ConnectorEnum,
  EventhouseProvisionStatus,
  defaultConnectorRecord,
  getEventhouseProvisionState,
} from '@/types/connector'
import { useTranslation } from '@/i18n'

const REFRESH_INTERVAL = 5000

// FS-579: standard IntuiButton instead of the shadcn Button — its
// padding/size didn't match the rest of the product. size="lg" carries the
// 36px height with its matching px-3 padding and svg sizing.
const PageActions = ({ onCreateClick, label }: { onCreateClick: () => void; label: string }) => (
  <IntuiButton size="lg" onClick={onCreateClick}>
    <Upload />
    {label}
  </IntuiButton>
)

const Connectors = () => {
  const {
    connectors,
    showConnectorDetailsModal,
    fetchConnectors,
    setConnectorEditData,
    setShowConnectorDetailsModal,
    advanceEventhouseProvision,
  } = useAppStore()
  const { t } = useTranslation()

  // Calls fetchConnectors on mount and every 5 seconds
  useEffect(() => {
    fetchConnectors()
    const connectorInterval = setInterval(() => {
      fetchConnectors()
    }, REFRESH_INTERVAL)
    return () => {
      clearInterval(connectorInterval)
    }
  }, [fetchConnectors])

  // Auto-advance seeded eventhouse provisions: once the worker stamps
  // 'seeded' the poll picks it up here and fires the finalize endpoint
  // (link + harvest). The slice's in-flight set dedupes across poll ticks.
  //
  // `!state.error` is what stops a FAILED link from looping forever: on link
  // failure the backend deliberately keeps status='seeded' (link is
  // idempotent, so the next attempt can succeed) and records
  // provision_error. Without this guard the 5s poll would re-fire the
  // endpoint every tick — an unbounded retry storm with a toast each time.
  // A recorded error hands control to the user's Retry button instead.
  useEffect(() => {
    for (const connector of connectors) {
      if (connector.type !== ConnectorEnum.EVENTHOUSE) continue
      const state = getEventhouseProvisionState(connector)
      if (
        state.status === EventhouseProvisionStatus.SEEDED &&
        !state.connectionId &&
        !state.error
      ) {
        void advanceEventhouseProvision(connector.id)
      }
    }
  }, [connectors, advanceEventhouseProvision])

  const resetConnectorConfigData = () => {
    setConnectorEditData(defaultConnectorRecord())
  }

  const handleModalClose = () => {
    setShowConnectorDetailsModal(false)
    resetConnectorConfigData()
  }

  return (
    <PageLayout
      title={t('connectors:page_title')}
      pageTools={() => (
        <PageActions
          onCreateClick={() => {
            resetConnectorConfigData()
            setShowConnectorDetailsModal(true)
          }}
          label={t('connectors:create_button')}
        />
      )}
    >
      <div className="border rounded-lg">
        <ConnectorDataTable data={connectors} />
      </div>
      <ConnectorEditModal
        isOpen={showConnectorDetailsModal}
        onClose={handleModalClose}
      />
    </PageLayout>
  )
}

export default Connectors
